/**
 * Conciliation Batch Store
 * 
 * Uses Zustand persist middleware to localStorage.
 * This is intentional - batches need to survive page refresh during
 * multi-step workflow completion (CSV upload → mapping → generation).
 * 
 * TODO [PHASE 2]: Migrate to Supabase storage
 * - Batches will be stored server-side in `conciliation_batches` table
 * - Remove persist middleware and fetch on component mount
 * - Add real-time subscription for batch status updates
 * 
 * @module features/conciliation/store/batchStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ConciliationBatchWithDetails } from '@/shared/types/batch.types';
import { log } from '@/lib/logging';
import { AUTH_STORAGE_KEY, isSessionOnlyMode } from '@/integrations/supabase/rememberMeStorage';

interface BatchStore {
  batches: ConciliationBatchWithDetails[];
  _orgId: string | null;
  _businessId: string | null;
  addBatch: (batch: ConciliationBatchWithDetails) => void;
  getBatches: () => ConciliationBatchWithDetails[];
  deleteBatch: (id: string) => void;
  setOrgId: (orgId: string) => void;
  setBusinessId: (businessId: string) => void;
  clearForOrgSwitch: () => void;
}

export const useBatchStore = create<BatchStore>()(
  persist(
    (set, get) => ({
      batches: [],
      _orgId: null,
      _businessId: null,
      addBatch: (batch) => {
        log('INFO', 'BATCH_STORE_ADD', { batchId: batch.id, courtId: batch.court_id });
        set((state) => ({
          batches: [batch, ...state.batches]
        }));
      },
      getBatches: () => get().batches,
      deleteBatch: (id) => {
        log('INFO', 'BATCH_STORE_DELETE', { batchId: id });
        set((state) => ({
          batches: state.batches.filter(b => b.id !== id)
        }));
      },
      setOrgId: (orgId) => {
        const current = get()._orgId;
        if (current && current !== orgId) {
          log('INFO', 'BATCH_STORE_ORG_SWITCH', { from: current, to: orgId });
          set({ batches: [], _orgId: orgId, _businessId: null });
        } else if (!current) {
          set({ _orgId: orgId });
        }
      },
      setBusinessId: (businessId) => {
        const current = get()._businessId;
        if (current && current !== businessId) {
          log('INFO', 'BATCH_STORE_BUSINESS_SWITCH', { from: current, to: businessId });
          set({ batches: [], _businessId: businessId });
        } else if (!current) {
          set({ _businessId: businessId });
        }
      },
      clearForOrgSwitch: () => {
        log('INFO', 'BATCH_STORE_CLEAR_ORG_SWITCH');
        set({ batches: [], _orgId: null, _businessId: null });
      },
    }),
    {
      name: 'conciliation-batches',
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Validate stored org matches current auth session to prevent cross-tenant data flash
          try {
            const storage = isSessionOnlyMode() ? sessionStorage : localStorage;
            const sessionRaw = storage.getItem(AUTH_STORAGE_KEY);
            if (sessionRaw && state._orgId) {
              const session = JSON.parse(sessionRaw);
              const currentOrgId = session?.user?.app_metadata?.org_id;
              if (currentOrgId && currentOrgId !== state._orgId) {
                log('INFO', 'BATCH_STORE_REHYDRATE_ORG_MISMATCH', {
                  storeOrg: state._orgId, sessionOrg: currentOrgId,
                });
                state.batches = [];
                state._orgId = null;
                state._businessId = null;
                return;
              }
            }
          } catch {
            // If session parsing fails, don't block rehydration
          }
          log('INFO', 'BATCH_STORE_REHYDRATED', { orgId: state._orgId, batchCount: state.batches.length });
        }
      },
    }
  )
);
